// index.tsx
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import HowItWorks from "./pages/HowItWorks";
import Layout from "./components/Layout";
import { AppConfigProvider } from "./lib/appConfig";
import { initAnalytics, trackPageview, analyticsEnabled } from "./lib/analytics";

const container = document.getElementById("root")!;

const path = window.location.pathname;
const page = path.startsWith("/terms")
  ? <Terms />
  : path.startsWith("/privacy")
    ? <Privacy />
    : path.startsWith("/billing/success")
      ? <BillingSuccess />
      : path.startsWith("/billing/cancel")
        ? <BillingCancel />
        : path.startsWith("/how-it-works")
          ? <HowItWorks />
          : <App />;

const shouldShowHeader = !(
  path.startsWith("/billing/success") ||
  path.startsWith("/billing/cancel")
);


/**
 * Analytics observer that DOES NOT depend on react-router.
 * It listens to popstate and patched pushState/replaceState to detect SPA navigations.
 */
function AnalyticsObserverNoRouter() {
  useEffect(() => {
    if (!analyticsEnabled) return;

    const idle = (cb: () => void) =>
      ("requestIdleCallback" in window ? (window as any).requestIdleCallback(cb) : setTimeout(cb, 0));

    const send = () => idle(() => trackPageview(location.pathname + location.search));

    // Track initial load
    send();

    // Keep typed copies of originals, already bound to history
    const originalPushState = history.pushState.bind(history) as History["pushState"];
    const originalReplaceState = history.replaceState.bind(history) as History["replaceState"];

    const notify = () => window.dispatchEvent(new Event("analytics:navigation"));

    // Patch without using `this`
    history.pushState = ((data: any, unused: string, url?: string | URL) => {
      const ret = originalPushState(data, unused, url);
      notify();
      return ret;
    }) as History["pushState"];

    history.replaceState = ((data: any, unused: string, url?: string | URL) => {
      const ret = originalReplaceState(data, unused, url);
      notify();
      return ret;
    }) as History["replaceState"];

    const onPop = () => send();
    const onNav = () => send();
    window.addEventListener("popstate", onPop);
    window.addEventListener("analytics:navigation", onNav);

    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("analytics:navigation", onNav);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  return null;
}


createRoot(container).render(
  <React.StrictMode>
    <AnalyticsObserverNoRouter />
    <AppConfigProvider>
      <Layout showHeader={shouldShowHeader}>{page}</Layout>
    </AppConfigProvider>
  </React.StrictMode>
);

initAnalytics();
